/**
 * Custom pickup module — replaces Pickeasy.
 * Handles: toggle (shipping/pickup), store selection, date selection, cart attribute writes.
 * Reads config from window.__STORE_CONFIG__ (set by store-config.liquid).
 * Dispatches 'pickup-module:changed' event on every state change for checkout guard.
 * Subscribes to Dawn's PUB_SUB_EVENTS.cartUpdate for cart reactivity.
 */
(function () {
  'use strict';

  var config = window.__STORE_CONFIG__;
  if (!config || !config.stores) return;

  // State persists across reinits (survives DOM replacement)
  var state = {
    fulfillment: 'shipping',
    storeIndex: null,
    selectedDate: null
  };

  // --- Shared utilities ---

  // Cart-level date constraints from product metafields (seasonal items), rendered by
  // pickup-module.liquid as data-date-constraints:
  //   allowed: one array of ISO dates per product with custom.pickup_dates (all must match → intersection)
  //   from:    latest custom.pickup_from across cart items ('' = none)
  //   until:   earliest custom.pickup_until across cart items ('' = none)
  // Store openDays/closedDates still apply on top, so e.g. a Sunday date is only offered where open.
  function getDateConstraints() {
    var module = document.getElementById('pickup-module');
    var empty = { allowed: null, from: '', until: '' };
    if (!module || !module.dataset.dateConstraints) return empty;
    try {
      var raw = JSON.parse(module.dataset.dateConstraints);
      var allowed = null;
      (raw.allowed || []).forEach(function (list) {
        var set = {};
        list.forEach(function (d) { set[d] = true; });
        if (allowed === null) {
          allowed = set;
        } else {
          Object.keys(allowed).forEach(function (d) { if (!set[d]) delete allowed[d]; });
        }
      });
      return { allowed: allowed, from: raw.from || '', until: raw.until || '' };
    } catch (e) {
      console.error('[pickup-module] Invalid date constraints:', e);
      return empty;
    }
  }

  function toIso(d) {
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  function getValidDates(store, count) {
    var now = new Date();
    var cutoff = config.cutoffHour;
    var start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (now.getHours() >= cutoff) {
      start.setDate(start.getDate() + 2);
    } else {
      start.setDate(start.getDate() + 1);
    }

    var constraints = getDateConstraints();
    // ISO strings compare correctly as plain strings
    var lastAllowed = null;
    if (constraints.allowed) {
      var keys = Object.keys(constraints.allowed).sort();
      if (keys.length === 0) return []; // products in cart share no pickup day
      lastAllowed = keys[keys.length - 1];
    }
    if (constraints.until && (!lastAllowed || constraints.until < lastAllowed)) {
      lastAllowed = constraints.until;
    }
    if (constraints.from) {
      var p = constraints.from.split('-');
      var fromDate = new Date(+p[0], +p[1] - 1, +p[2]);
      if (fromDate > start) start = fromDate;
    }

    var closedSet = {};
    (store.closedDates || []).forEach(function (d) { closedSet[d] = true; });
    var dates = [];
    var candidate = new Date(start);
    var maxIter = 60;
    while (dates.length < count && maxIter-- > 0) {
      var iso = toIso(candidate);
      if (lastAllowed && iso > lastAllowed) break;
      var allowedHere = !constraints.allowed || constraints.allowed[iso];
      if (allowedHere && store.openDays.indexOf(candidate.getDay()) !== -1 && !closedSet[iso]) {
        dates.push({ date: new Date(candidate), iso: iso });
      }
      candidate.setDate(candidate.getDate() + 1);
    }
    return dates;
  }

  // True when no store offers any date (e.g. Weckmann + Adventskalender: no shared pickup day)
  function hasNoPickupDates() {
    return config.stores.every(function (s) {
      return getValidDates(s, 1).length === 0;
    });
  }

  function formatDateButton(d) {
    var days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return days[d.date.getDay()] + ' ' +
      String(d.date.getDate()).padStart(2, '0') + '.' +
      String(d.date.getMonth() + 1).padStart(2, '0') + '.';
  }

  function formatSummary(store, d) {
    var days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    var text = 'Abholung: ' + store.name + ' am ' + days[d.date.getDay()] + ', ' +
      String(d.date.getDate()).padStart(2, '0') + '.' +
      String(d.date.getMonth() + 1).padStart(2, '0') + '.' + d.date.getFullYear();
    if (store.address) {
      text += '\n' + store.address;
    }
    return text;
  }

  function getOpeningHours(store, dayOfWeek) {
    if (!store.hours) return null;
    return store.hours[dayOfWeek] || null;
  }

  function writeAttributes(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
      return res.json();
    });
  }

  function setPickupAttributes(storeName, isoDate) {
    return writeAttributes({
      'Bestelltyp': 'Abholung',
      'Abholort': storeName,
      'Abholdatum': isoDate
    });
  }

  function clearPickupAttributes() {
    return writeAttributes({
      'Bestelltyp': 'Versand',
      'Abholort': '',
      'Abholdatum': ''
    });
  }

  function emitChange() {
    var event;
    try {
      event = new CustomEvent('pickup-module:changed', { detail: state });
    } catch (e) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent('pickup-module:changed', true, true, state);
    }
    document.dispatchEvent(event);
  }

  // --- Get checkout hint message based on current state ---
  // Seasonal custom.ship_until passed for an item (set by pickup-module.liquid)
  function isShipBlocked() {
    var module = document.getElementById('pickup-module');
    return !!module && module.dataset.shipBlocked === 'true';
  }

  function getCheckoutHint() {
    if (state.fulfillment !== 'pickup') {
      return isShipBlocked() ? 'Versand für einen Artikel nicht mehr möglich – bitte entfernen' : null;
    }
    if (hasNoPickupDates()) return 'Kein Abholtag für deinen Warenkorb verfügbar – Artikel ggf. getrennt bestellen';
    if (state.storeIndex === null) return 'Standort und Abholtag wählen';
    if (!state.selectedDate) return 'Abholtag wählen';
    return null; // Complete — no hint needed
  }

  // --- Init: binds to current DOM elements ---
  // Called on page load and after DOM replacement (cart update)

  function init() {
    var module = document.getElementById('pickup-module');
    if (!module) return;

    var mode = module.dataset.mode;
    var inner = document.getElementById('pickup-inner');
    var storeButtonsContainer = document.getElementById('store-buttons');
    var datesContainer = document.getElementById('pickup-dates');
    var dateGrid = document.getElementById('date-grid');
    var summaryEl = document.getElementById('pickup-summary');
    var summaryText = document.getElementById('pickup-summary-text');
    var deliveryDate = document.getElementById('pickup-delivery-date');
    var hoursEl = document.getElementById('pickup-hours');

    // Update state.fulfillment based on current mode
    if (mode === 'pickup_only') {
      state.fulfillment = 'pickup';
    } else if (mode === 'shipping_only' || mode === 'hidden') {
      state.fulfillment = 'shipping';
      state.storeIndex = null;
      state.selectedDate = null;
      emitChange();
      return; // No interactive elements needed
    }
    // toggle mode: keep current state.fulfillment (may be restored)

    // Render store buttons from config
    if (storeButtonsContainer) {
      storeButtonsContainer.innerHTML = '';
      config.stores.forEach(function (store, idx) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pickup-module__store-btn';
        if (state.storeIndex === idx) btn.className += ' pickup-module__store-btn--active';
        btn.textContent = store.name;
        btn.dataset.storeIndex = idx;
        storeButtonsContainer.appendChild(btn);
      });
    }

    // Render dates if store was already selected
    if (state.storeIndex !== null && dateGrid) {
      renderDates(state.storeIndex, dateGrid, datesContainer, summaryEl, summaryText, hoursEl);
    }

    // --- Toggle logic ---
    if (mode === 'toggle') {
      var toggleShipping = document.getElementById('toggle-shipping');
      var togglePickup = document.getElementById('toggle-pickup');

      function applyToggle() {
        if (state.fulfillment === 'shipping') {
          if (toggleShipping) toggleShipping.classList.add('pickup-module__toggle-btn--active');
          if (togglePickup) togglePickup.classList.remove('pickup-module__toggle-btn--active');
          if (inner) inner.style.display = 'none';
          if (deliveryDate) deliveryDate.style.display = '';
        } else {
          if (togglePickup) togglePickup.classList.add('pickup-module__toggle-btn--active');
          if (toggleShipping) toggleShipping.classList.remove('pickup-module__toggle-btn--active');
          if (inner) inner.style.display = '';
          if (deliveryDate) deliveryDate.style.display = 'none';
        }
        emitChange();
      }

      // Saved pickup selections survive toggle to shipping and back
      var savedPickup = { storeIndex: null, selectedDate: null };

      if (toggleShipping) {
        toggleShipping.addEventListener('click', function () {
          // Save current pickup selections before clearing
          savedPickup.storeIndex = state.storeIndex;
          savedPickup.selectedDate = state.selectedDate;
          state.fulfillment = 'shipping';
          state.storeIndex = null;
          state.selectedDate = null;
          clearPickupAttributes();
          applyToggle();
        });
      }
      if (togglePickup) {
        togglePickup.addEventListener('click', function () {
          state.fulfillment = 'pickup';
          // Restore saved pickup selections
          if (savedPickup.storeIndex !== null) {
            state.storeIndex = savedPickup.storeIndex;
            state.selectedDate = savedPickup.selectedDate;
            // Re-render store/date UI with restored state
            renderDates(state.storeIndex, dateGrid, datesContainer, summaryEl, summaryText, hoursEl);
            // Re-apply store button active state
            if (storeButtonsContainer) {
              var allBtns = storeButtonsContainer.querySelectorAll('.pickup-module__store-btn');
              for (var i = 0; i < allBtns.length; i++) {
                allBtns[i].classList.toggle('pickup-module__store-btn--active', i === state.storeIndex);
              }
            }
            // Re-write cart attributes if fully selected
            if (state.selectedDate) {
              var store = config.stores[state.storeIndex];
              setPickupAttributes(store.name, state.selectedDate);
            }
          }
          applyToggle();
        });
      }

      // Apply current state to DOM
      applyToggle();
    }

    // --- Store selection (event delegation on container) ---
    if (storeButtonsContainer) {
      storeButtonsContainer.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-store-index]');
        if (!btn) return;

        var idx = parseInt(btn.dataset.storeIndex, 10);
        state.storeIndex = idx;
        state.selectedDate = null;

        var allBtns = storeButtonsContainer.querySelectorAll('.pickup-module__store-btn');
        for (var i = 0; i < allBtns.length; i++) {
          allBtns[i].classList.toggle('pickup-module__store-btn--active', i === idx);
        }

        renderDates(idx, dateGrid, datesContainer, summaryEl, summaryText, hoursEl);
        clearPickupAttributes();
        emitChange();
      });
    }

    // --- Date selection (event delegation on grid) ---
    if (dateGrid) {
      dateGrid.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-iso]');
        if (!btn) return;

        var iso = btn.dataset.iso;
        state.selectedDate = iso;

        var allBtns = dateGrid.querySelectorAll('.pickup-module__date-btn');
        for (var i = 0; i < allBtns.length; i++) {
          allBtns[i].classList.toggle('pickup-module__date-btn--active', allBtns[i].dataset.iso === iso);
        }

        var store = config.stores[state.storeIndex];
        var dateObj = getValidDates(store, config.dateCount || 8).find(function (d) { return d.iso === iso; });

        if (store && dateObj) {
          // Update summary
          if (summaryText && summaryEl) {
            summaryText.textContent = formatSummary(store, dateObj);
            summaryEl.style.display = '';
          }
          // Update opening hours for selected day
          updateHoursDisplay(store, dateObj.date.getDay(), hoursEl);
          setPickupAttributes(store.name, iso);
        }

        emitChange();
      });
    }

    emitChange();
  }

  function updateHoursDisplay(store, dayOfWeek, hoursEl) {
    if (!hoursEl) return;
    var hours = getOpeningHours(store, dayOfWeek);
    if (hours) {
      var days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
      hoursEl.textContent = 'Öffnungszeiten ' + days[dayOfWeek] + ': ' + hours;
      hoursEl.style.display = '';
    } else {
      hoursEl.style.display = 'none';
    }
  }

  function renderDates(storeIdx, dateGrid, datesContainer, summaryEl, summaryText, hoursEl) {
    if (!dateGrid) return;
    var store = config.stores[storeIdx];
    var dates = getValidDates(store, config.dateCount || 8);

    // A restored or previously picked date may no longer be valid after the cart changed
    // (e.g. Weckmann added) — drop it so checkout is blocked until a valid day is chosen.
    if (state.selectedDate && !dates.some(function (d) { return d.iso === state.selectedDate; })) {
      state.selectedDate = null;
      writeAttributes({ 'Abholdatum': '' }).catch(function (err) {
        console.error('[pickup-module] Failed to clear invalid date:', err);
      });
      emitChange();
    }

    dateGrid.innerHTML = '';
    if (dates.length === 0) {
      var msg = document.createElement('p');
      msg.className = 'pickup-module__no-dates';
      msg.textContent = hasNoPickupDates()
        ? 'Für die Artikel in deinem Warenkorb gibt es keinen gemeinsamen (oder keinen weiteren) Abholtag. Bitte prüfe die Abholtermine auf den Produktseiten und bestelle die Artikel ggf. getrennt.'
        : 'In dieser Filiale ist für deinen Warenkorb kein Abholtag verfügbar. Bitte wähle die andere Filiale.';
      dateGrid.appendChild(msg);
    }
    dates.forEach(function (d) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pickup-module__date-btn';
      if (state.selectedDate === d.iso) btn.className += ' pickup-module__date-btn--active';
      btn.textContent = formatDateButton(d);
      btn.dataset.iso = d.iso;
      dateGrid.appendChild(btn);
    });

    if (datesContainer) datesContainer.style.display = '';

    // Show summary and hours if date was already selected and still valid
    if (state.selectedDate) {
      var dateObj = dates.find(function (d) { return d.iso === state.selectedDate; });
      if (dateObj && summaryText && summaryEl) {
        summaryText.textContent = formatSummary(store, dateObj);
        summaryEl.style.display = '';
        updateHoursDisplay(store, dateObj.date.getDay(), hoursEl);
      }
    } else {
      if (summaryEl) summaryEl.style.display = 'none';
      if (hoursEl) hoursEl.style.display = 'none';
    }
  }

  // --- Restore state from existing cart attributes on page load ---
  function restoreState() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var attrs = cart.attributes || {};
        if (attrs['Bestelltyp'] !== 'Abholung') return;

        var storeName = attrs['Abholort'] || '';
        var dateIso = attrs['Abholdatum'] || '';
        if (!storeName || !dateIso) return;

        var storeIdx = -1;
        config.stores.forEach(function (s, i) {
          if (s.name === storeName) storeIdx = i;
        });
        if (storeIdx === -1) return;

        state.fulfillment = 'pickup';
        state.storeIndex = storeIdx;
        state.selectedDate = dateIso;

        // Re-init with restored state
        init();
      })
      .catch(function (err) {
        console.error('[pickup-module] Failed to restore state:', err);
      });
  }

  // --- Cart reactivity: re-fetch section on cart update ---
  function setupCartReactivity() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        var sectionId = null;
        var mainCartItems = document.getElementById('main-cart-items');
        if (mainCartItems) sectionId = mainCartItems.dataset.id;
        if (!sectionId) return;

        fetch(window.location.pathname + '?section_id=' + sectionId)
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var newModule = doc.getElementById('pickup-module');
            var currentModule = document.getElementById('pickup-module');

            if (newModule && currentModule) {
              currentModule.replaceWith(newModule);
              init();
            } else if (!newModule && currentModule) {
              currentModule.style.display = 'none';
              state.fulfillment = 'shipping';
              state.storeIndex = null;
              state.selectedDate = null;
              clearPickupAttributes();
              emitChange();
            } else if (newModule && !currentModule) {
              // Fallback: if module appears after AJAX cart change, insert it
              // after the store config script or at end of cart items section
              var anchor = document.getElementById('pickup-store-config')
                || document.querySelector('[data-store-config]')
                || document.getElementById('main-cart-items');
              if (anchor) {
                anchor.insertAdjacentElement('afterend', newModule);
              }
              init();
            }
          })
          .catch(function (err) {
            console.error('[pickup-module] Section re-fetch failed:', err);
          });
      });
    }
  }

  // Expose state reader and hint getter for checkout guard
  window.__PICKUP_MODULE__ = {
    getState: function () { return state; },
    getCheckoutHint: getCheckoutHint,
    getMode: function () {
      var module = document.getElementById('pickup-module');
      return module ? module.dataset.mode : 'hidden';
    }
  };

  // Boot
  init();
  restoreState();
  setupCartReactivity();
})();
